import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LockIcon from "@mui/icons-material/Lock";
import MailIcon from "@mui/icons-material/Mail";
import PersonIcon from "@mui/icons-material/Person";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useChangePassword, useRemoveOauth2Client, useUser } from "model";
import { useState } from "react";

import { useNotification } from "app/NotificationProvider";
import { AnalyticsEvents, analytics } from "common/analytics";
import AgriwebbLogoIcon from "common/icons/AgriwebbLogoIcon";
import { useTranslation } from "common/locales";
import {
  ExternalProvider,
  externalProviderName,
} from "features/signup/externalProviders";
import { useIntl } from "react-intl";
import { logout } from "services/auth";
import { useAppStore } from "stores";

interface ProfileDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function ProfileDialog({ open, onClose }: ProfileDialogProps) {
  const user = useAppStore((state) => state.user);
  const { formatMessage: f } = useIntl();
  const [editingPassword, setEditingPassword] = useState(false);
  const [password, setPassword] = useState("");

  const { notify } = useNotification();
  const { t } = useTranslation();

  // React Query mutations
  const changePasswordMutation = useChangePassword();
  const removeOauth2ClientMutation = useRemoveOauth2Client();

  // Refetch user data after oauth2 removal
  const { refetch: refetchUser } = useUser(user?.guid, open);

  const handlePasswordSubmit = async () => {
    if (password && user) {
      await changePasswordMutation.mutateAsync({
        userId: user.guid,
        newPassword: password,
      });
      notify(t("passwordChange.success"));
      setEditingPassword(false);

      analytics.track(AnalyticsEvents.PASSWORD_CHANGED);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleClose = () => {
    onClose();
    setEditingPassword(false);
  };

  const removeIntegration = async (externalProvider: ExternalProvider) => {
    if (!user) return;
    await removeOauth2ClientMutation.mutateAsync({
      userId: user.guid,
      externalProvider,
    });
    await refetchUser();
    analytics.track(AnalyticsEvents.INTEGRATION_REMOVED, {
      provider: externalProviderName[externalProvider],
    });
    notify(
      t("oauth2ClientRemove.success", {
        providerName: externalProviderName[externalProvider],
      })
    );
  };

  if (!user) {
    return <></>;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="profile-dialog-title"
    >
      <ProfileDialogTitle id="profile-dialog-title">
        <Typography variant="h6">{f({ id: "settings.title" })}</Typography>
        <CloseButton onClick={handleClose}>
          <CloseIcon />
        </CloseButton>
      </ProfileDialogTitle>
      <DialogContent dividers>
        <Profile>
          <ListItem>
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText
              primary={user.name}
              secondary={f({ id: "settings.name" })}
              data-private
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <MailIcon />
            </ListItemIcon>
            <ListItemText
              primary={user.mail}
              secondary={f({ id: "settings.mail" })}
              data-private
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <LockIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                editingPassword ? (
                  <Input
                    id="input-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    type="password"
                  />
                ) : (
                  f({ id: "settings.password" })
                )
              }
            />
            <ListItemSecondaryAction>
              {editingPassword ? (
                <IconButton onClick={handlePasswordSubmit} size="large">
                  <CheckIcon />
                </IconButton>
              ) : (
                <IconButton
                  edge="end"
                  aria-label="edit-password"
                  onClick={() => setEditingPassword(true)}
                  size="large"
                >
                  <EditIcon />
                </IconButton>
              )}
            </ListItemSecondaryAction>
          </ListItem>
          {user.oauth2clients &&
            user.oauth2clients.includes(ExternalProvider.AGRIWEBB) && (
              <ListItem>
                <ListItemIcon>
                  <AgriwebbLogoIcon />
                </ListItemIcon>
                <ListItemText primary={f({ id: "settings.agriwebb" })} />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label={f({ id: "settings.agriwebb.remove" })}
                    onClick={() => removeIntegration(ExternalProvider.AGRIWEBB)}
                    size="large"
                    title={f({ id: "settings.agriwebb.remove" })}
                  >
                    <ClearIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            )}
        </Profile>
        <Divider />
        <List>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary={f({ id: "settings.logout" })} />
          </ListItemButton>
        </List>
      </DialogContent>
    </Dialog>
  );
}

const ProfileDialogTitle = styled(DialogTitle)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2),
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(1),
  top: theme.spacing(1),
}));

const Profile = styled(List)({
  paddingTop: 0,
  minWidth: "300px",
});
